import type { ClientSummary, ProjectSummary, WebsiteSummary } from "../api/types.js";

/**
 * Authorized tiny mock — not billed data, not a billing module.
 * Kept as a constant for compatibility; dashboard prefers real counts over this slot.
 */
export const ILLUSTRATIVE_MONTHLY_REVENUE = "890 €";

export type ActivityKind = "client" | "website" | "project";

export interface ActivityItem {
  readonly id: string;
  readonly at: string;
  readonly title: string;
  readonly kind: ActivityKind;
  readonly entityId: string;
  readonly clientId: string;
}

export interface UpcomingItem {
  readonly id: string;
  readonly name: string;
  readonly plannedCompletion: string;
  readonly clientId: string;
}

const MS_PER_DAY = 86_400_000;

const byUpdatedDesc = (left: ActivityItem, right: ActivityItem): number =>
  new Date(right.at).getTime() - new Date(left.at).getTime();

const catalogActivity = (
  clients: readonly ClientSummary[],
  websites: readonly WebsiteSummary[],
  projects: readonly ProjectSummary[],
): readonly ActivityItem[] => [
  ...clients.map((client): ActivityItem => ({
    id: `client:${client.id}`,
    at: client.updatedAt,
    title: client.name,
    kind: "client",
    entityId: client.id,
    clientId: client.id,
  })),
  ...websites.map((website): ActivityItem => ({
    id: `website:${website.id}`,
    at: website.updatedAt,
    title: website.domain,
    kind: "website",
    entityId: website.id,
    clientId: website.clientId,
  })),
  ...projects.map((project): ActivityItem => ({
    id: `project:${project.id}`,
    at: project.updatedAt,
    title: project.name,
    kind: "project",
    entityId: project.id,
    clientId: project.clientId,
  })),
];

export const recentActivity = (
  clients: readonly ClientSummary[],
  websites: readonly WebsiteSummary[],
  projects: readonly ProjectSummary[],
  limit: number,
): readonly ActivityItem[] =>
  [...catalogActivity(clients, websites, projects)].sort(byUpdatedDesc).slice(0, limit);

/**
 * Count clients + websites + projects whose updatedAt falls within the last `days`.
 * Derived from existing list timestamps — not a new analytics KPI.
 */
export const countUpdatedWithinDays = (
  clients: readonly ClientSummary[],
  websites: readonly WebsiteSummary[],
  projects: readonly ProjectSummary[],
  days: number,
  now: Date = new Date(),
): number => {
  const since = now.getTime() - days * MS_PER_DAY;
  return catalogActivity(clients, websites, projects).filter((item) => {
    const at = new Date(item.at).getTime();
    return !Number.isNaN(at) && at >= since;
  }).length;
};

export const latestByUpdated = <T extends { readonly updatedAt: string }>(
  items: readonly T[],
  limit: number,
): readonly T[] =>
  [...items].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)).slice(0, limit);

/**
 * Newest updatedAt across client + its websites + projects already in memory.
 * Not an analytics KPI — meeting scan signal only.
 */
export const latestActivityAt = (
  clientUpdatedAt: string,
  websites: readonly { readonly updatedAt: string }[],
  projects: readonly { readonly updatedAt: string }[],
): string => {
  let latest = clientUpdatedAt;
  for (const item of websites) {
    if (item.updatedAt.localeCompare(latest) > 0) {
      latest = item.updatedAt;
    }
  }
  for (const item of projects) {
    if (item.updatedAt.localeCompare(latest) > 0) {
      latest = item.updatedAt;
    }
  }
  return latest;
};

export const upcomingFromProjects = (
  projects: readonly ProjectSummary[],
  limit: number,
): readonly UpcomingItem[] => {
  const dated = projects.flatMap((project): readonly UpcomingItem[] => {
    if (project.plannedCompletion === null) {
      return [];
    }
    return [
      {
        id: project.id,
        name: project.name,
        plannedCompletion: project.plannedCompletion,
        clientId: project.clientId,
      },
    ];
  });
  return [...dated]
    .sort((left, right) => left.plannedCompletion.localeCompare(right.plannedCompletion))
    .slice(0, limit);
};
